import {
  useGetAgentById,
  usePublishAgent,
  useUpdateAgentAIConfigurations,
  useUpdateWidget,
} from '@/modules/gpt-chats/hooks/use-agents';
import { useState, useRef, useMemo } from 'react';
import useIsMobile from '@/hooks/use-is-mobile';
import { BasePromptAIModal } from './components/config-card-section/llm-section/base-prompt-ai-modal';
import { AgentsConfigPreviewSection } from './components/preview-section/agents-config-preview-section';
import { AgentConfigCardForm } from './components/config-card-section/agent-config-card-form';
import { useAutoSave } from './hooks/use-auto-save';
import { useAgentForm } from './hooks/use-agent-form';
import { useAgentSubmit } from './hooks/use-agent-submit';
import { EditAgentModal } from '@/modules/gpt-chats/components/agents-configuration/components/edit-agent-modal/edit-agent-modal';
import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import { ArrowLeft, PenLine } from 'lucide-react';
import { showErrorToast, showSuccessToast } from '@/hooks/use-toast';
import { ErrorTransformer } from '@/modules/gpt-chats/utils/error-transform';
import { LastUpdatedInfo } from './components/config-card-section/last-updated-info';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui-kit/tooltip';


type AgentsConfigurationProps = {
  agentId: string;
};

export const AgentsConfiguration = ({ agentId }: AgentsConfigurationProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isPersonaModalOpen, setIsPersonaModalOpen] = useState(false);
  const [isBasePromptAIModalOpen, setIsBasePromptAIModalOpen] = useState(false);
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  // const [_llmSource, setLlmSource] = useState<'blocks' | 'custom'>('blocks');

  const { data } = useGetAgentById(agentId);
  const { mutateAsync } = useUpdateAgentAIConfigurations();
  const { mutateAsync: updateWidget } = useUpdateWidget();
  const { mutateAsync: publishAgent, isPending: isPublishing } = usePublishAgent();

  const isMobile = useIsMobile();
  const is2XlBreakpoint = useIsMobile();
  const isInitialMount = useRef(true);

  const navigate = useNavigate();

  const form = useAgentForm(data?.agent);
  const { onSubmit, isSaving, lastSaveTime } = useAgentSubmit({
    agentId,
    data,
    form,
    mutateAsync: mutateAsync as never,
    updateWidget: updateWidget as never,
  });

  const { watch } = form;
  const temperature = watch('llm_config.temperature');
  const maxTokens = watch('llm_config.max_tokens');
  const scoreThreshold = watch('rag_config.score_threshold');
  const topK = watch('rag_config.top_k');
  const denseWeight = watch('rag_config.dense_weight');
  const keywordWeight = watch('rag_config.keyword_weight');
  const enableQueryEnhancement = watch('rag_config.enable_query_enhancement');
  const modelName = watch('llm_config.model_name');
  const basePrompt = watch('llm_config.base_prompt');
  const greeting = watch('widget.greeting');

  const enableSummary = watch('memory_config.enable_summary');
  const enableQuestionSuggestion = watch('memory_config.enable_questions_suggestions');

  const inputRiskThreshold = watch('guardrail.input_risk_threshold');
  const outputRiskThreshold = watch('guardrail.output_risk_threshold');
  const responseType = watch('response_type');
  const responseFormat = watch('response_format');

  // useEffect(() => {
  //   if (data?.agent && isInitialMount.current) {
  //     const isBlocksProvider = data.agent.llm_config?.is_blocks_provider ?? true;
  //     // setLlmSource(isBlocksProvider ? 'blocks' : 'custom');
  //   }
  // }, [data?.agent]);

  const autoSaveFields = useMemo(
    () => ({
      temperature: { value: temperature, delay: 1000, onSave: onSubmit },
      maxTokens: { value: maxTokens, delay: 1000, onSave: onSubmit },
      scoreThreshold: { value: scoreThreshold, delay: 1000, onSave: onSubmit },
      topK: { value: topK, delay: 1000, onSave: onSubmit },
      searchWeightConfig: {
        value: `${denseWeight}|${keywordWeight}`,
        delay: 1500,
        onSave: onSubmit,
      },
      enableQueryEnhancement: { value: enableQueryEnhancement, delay: 500, onSave: onSubmit },
      enableQuestionSuggestion: { value: enableQuestionSuggestion, delay: 500, onSave: onSubmit },
      modelName: { value: modelName, delay: 500, onSave: onSubmit },
      enableSummary: { value: enableSummary, delay: 500, onSave: onSubmit },
      inputRiskThreshold: { value: inputRiskThreshold, delay: 1000, onSave: onSubmit },
      outputRiskThreshold: { value: outputRiskThreshold, delay: 1000, onSave: onSubmit },
      basePrompt: { value: basePrompt, delay: 1000, onSave: onSubmit },
      greeting: { value: greeting, delay: 1000, onSave: onSubmit },
      responseConfig: { value: `${responseType}|${responseFormat}`, delay: 500, onSave: onSubmit },
    }),
    [
      temperature,
      maxTokens,
      scoreThreshold,
      topK,
      denseWeight,
      keywordWeight,
      enableQueryEnhancement,
      enableQuestionSuggestion,
      modelName,
      enableSummary,
      inputRiskThreshold,
      outputRiskThreshold,
      basePrompt,
      greeting,
      responseType,
      responseFormat,
      onSubmit,
    ]
  );

  useAutoSave({
    form,
    isInitialMount,
    hasData: !!data?.agent,
    fields: autoSaveFields,
  });

  const handlePublish = async () => {
    try {
      const response = await publishAgent({
        agent_id: agentId,
        // project_key: tenantId,
      });

      if (response.is_success) {
        showSuccessToast({ description: t('AGENT_PUBLISHED_SUCCESSFULLY') });
        queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
      } else {
        showErrorToast({ errors: ErrorTransformer(response.detail || response.error) });
      }
    } catch (error) {
      showErrorToast({ errors: ErrorTransformer(error) });
    }
  };

  const agentName = data?.agent?.name;

  const shouldShowMobilePreview = is2XlBreakpoint;

  const handleShowPreview = () => {
    setShowMobilePreview(true);
    // if (shouldShowMobilePreview && setIsOpen) {
    //   setIsOpen(false);
    // }
  };

  const handleCloseMobilePreview = () => {
    setShowMobilePreview(false);
  };

  return (
    <div className="flex gap-0">
      <div className="flex-1 overflow-y-hidden">
        <div className="mb-6 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-col gap-2">
            <div className="flex gap-4">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-5 cursor-pointer"
                    onClick={() => {
                      queryClient.invalidateQueries({ queryKey: ['agent-conversation-list'] });
                      queryClient.invalidateQueries({ queryKey: ['conversations'] });
                      navigate('/ai/agents');
                    }}
                  >
                    <ArrowLeft className="text-primary" size={20} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('BACK_TO_AGENTS')}</p>
                </TooltipContent>
              </Tooltip>
              <h3
                className="max-w-[400px] truncate text-2xl font-bold tracking-tight"
                title={agentName}
              >
                {agentName}
              </h3>

              <Button
                variant="ghost"
                size="icon"
                className="mt-2 h-5 w-5 cursor-pointer text-primary"
                onClick={() => setIsPersonaModalOpen(true)}
              >
                <PenLine size={20} />
              </Button>
            </div>
            {data?.agent && (
              <Badge variant="outline" className="w-fit bg-border">
                {!data.agent.is_any_agent
                  ? t('AGENT_TYPE_SEQUENTIAL_NAME')
                  : t('AGENT_TYPE_REASONING_NAME')}{' '}
                {t('AGENT')}
              </Badge>
            )}
          </div>

          <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
            <span className="flex items-center gap-2 text-sm text-medium-emphasis">
              {t('LAST_PUBLISHED')}:{' '}
              <LastUpdatedInfo
                isSaving={isPublishing}
                lastSaveTime={null}
                lastUpdatedDate={data?.agent?.published_date}
                savingText={t('PUBLISHING')}
                labelText=""
              />
            </span>
            <Button
              onClick={handlePublish}
              disabled={isPublishing}
              // size="xs"
              className="items-center justify-center gap-2.5 px-5 py-1.5 text-sm"
            >
              {t('PUBLISH')}
            </Button>
          </div>
        </div>
        {/* Config Card */}
        <AgentConfigCardForm
          form={form}
          agentId={agentId}
          isMobile={isMobile}
          showPreviewButton={shouldShowMobilePreview}
          isSaving={isSaving}
          lastSaveTime={lastSaveTime}
          lastUpdatedDate={data?.agent?.last_updated_date}
          onSubmit={onSubmit}
          onShowPreview={handleShowPreview}
          onOpenBasePromptAIModal={() => setIsBasePromptAIModalOpen(true)}
        />
      </div>

      {/* Preview Section */}
      {!shouldShowMobilePreview && (
        <AgentsConfigPreviewSection
          key={agentId}
          agentId={agentId}
          isMobile={isMobile}
          showMobilePreview={showMobilePreview}
          onCloseMobilePreview={handleCloseMobilePreview}
        />
      )}

      <EditAgentModal
        agentId={agentId}
        open={isPersonaModalOpen}
        onOpenChange={setIsPersonaModalOpen}
      />

      <BasePromptAIModal
        isOpen={isBasePromptAIModalOpen}
        onClose={() => setIsBasePromptAIModalOpen(false)}
        onUse={(basePrompt) => form.setValue('llm_config.base_prompt', basePrompt)}
        agentName={agentName}
        basePromptValue={form.watch('llm_config.base_prompt') as string}
        tool_ids={form.watch('tool_ids')}
      />
    </div>
  );
};
